/* Godard fix: lightweight auto-playing, swipeable carousel for the "You may
   also like" recommendations row. No external library - plain scroll-snap +
   a small amount of JS for arrows, dots, drag and autoplay. Pauses on
   hover/touch/focus/drag and respects prefers-reduced-motion. Safe to include
   site-wide: it only ever acts on elements with the .godard-carousel class.

   Round 2 (2026-09-12):
   - Added click-and-drag (pointer events) on desktop; previously only native
     touch scrolling worked, so there was no way to drag with a mouse.
   - Arrows now advance a whole visible page instead of a single card, and
     clamp to the ends rather than relying on smooth-scroll overshoot.
   - Scroll snapping is suspended for the duration of a scripted scroll, and
     an animated scroll that never starts falls back to an instant jump, so
     the arrows can never look dead.
   - Arrow state is recomputed after images finish loading, because
     scrollWidth is wrong while the product images are still undecoded - that
     could leave "next" disabled on a carousel that does overflow.
   - Drag suppresses the click that would otherwise follow through to the
     product card link.

   Round 3 (2026-09-12):
   - Fixed: plain clicks on a recommended product were dead on desktop. The
     'is-dragging' class (which sets pointer-events:none on links/images) was
     added on pointerdown, before any movement, so a normal click's down and
     up targets differed and the anchor never navigated. It is now added only
     once a real drag is detected in pointermove, so clicks pass through. */
(function () {
  function initCarousel(root) {
    if (root.dataset.godardCarouselInit === 'true') return;
    root.dataset.godardCarouselInit = 'true';

    var viewport = root.querySelector('.godard-carousel__viewport');
    var track = root.querySelector('.godard-carousel__track');
    if (!viewport || !track) return;

    var prevBtn = root.querySelector('.godard-carousel__arrow--prev');
    var nextBtn = root.querySelector('.godard-carousel__arrow--next');
    var dotsWrap = root.querySelector('.godard-carousel__dots');
    var autoplayDelay = parseInt(root.getAttribute('data-autoplay'), 10) || 0;
    var timer = null;
    var prefersReducedMotion =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function items() {
      return Array.prototype.slice.call(track.querySelectorAll('.grid__item'));
    }

    function itemStep() {
      var list = items();
      if (list.length < 2) {
        return list.length ? list[0].getBoundingClientRect().width : viewport.clientWidth;
      }
      var step = list[1].getBoundingClientRect().left - list[0].getBoundingClientRect().left;
      return step > 0 ? step : list[0].getBoundingClientRect().width;
    }

    function pageStep() {
      var step = itemStep();
      if (step <= 0) return viewport.clientWidth;
      var perPage = Math.max(1, Math.floor((viewport.clientWidth + 1) / step));
      return step * perPage;
    }

    function maxScroll() {
      return Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    }

    function hasOverflow() {
      return maxScroll() > 2;
    }

    function updateArrows() {
      if (!prevBtn || !nextBtn) return;
      var max = maxScroll();
      if (max <= 2) {
        root.classList.add('godard-carousel--static');
        prevBtn.disabled = true;
        nextBtn.disabled = true;
        return;
      }
      root.classList.remove('godard-carousel--static');
      prevBtn.disabled = viewport.scrollLeft <= 2;
      nextBtn.disabled = viewport.scrollLeft >= max - 2;
    }

    function updateDots() {
      if (!dotsWrap) return;
      var dots = dotsWrap.querySelectorAll('.godard-carousel__dot');
      if (!dots.length) return;
      var max = maxScroll();
      var progress = max > 0 ? viewport.scrollLeft / max : 0;
      var activeIndex = Math.round(progress * (dots.length - 1));
      Array.prototype.forEach.call(dots, function (dot, i) {
        dot.classList.toggle('is-active', i === activeIndex);
      });
    }

    var snapRestoreTimer = null;
    function scrollTo(left) {
      var max = maxScroll();
      if (left < 0) left = 0;
      if (left > max) left = max;
      var from = viewport.scrollLeft;
      viewport.style.scrollSnapType = 'none';
      viewport.scrollTo({
        left: left,
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
      });
      window.setTimeout(function () {
        if (Math.abs(left - from) > 2 && Math.abs(viewport.scrollLeft - from) < 2) {
          viewport.scrollLeft = left;
        }
      }, 380);
      if (snapRestoreTimer) window.clearTimeout(snapRestoreTimer);
      snapRestoreTimer = window.setTimeout(function () {
        if (!dragging) viewport.style.scrollSnapType = '';
        snapRestoreTimer = null;
      }, 700);
    }

    function scrollByStep(direction) {
      scrollTo(viewport.scrollLeft + direction * pageStep());
    }

    function buildDots() {
      if (!dotsWrap) return;
      dotsWrap.innerHTML = '';
      var total = items().length;
      if (total < 2 || !hasOverflow()) return;
      var step = itemStep();
      var visibleCount = Math.max(1, Math.floor((viewport.clientWidth + 1) / step));
      var pageCount = Math.max(1, Math.ceil(total / visibleCount));
      if (pageCount < 2) return;
      for (var i = 0; i < pageCount; i++) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'godard-carousel__dot';
        dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        (function (index) {
          dot.addEventListener('click', function () {
            stopAutoplay();
            scrollTo((maxScroll() / (pageCount - 1 || 1)) * index);
          });
        })(i);
        dotsWrap.appendChild(dot);
      }
    }

    function startAutoplay() {
      if (!autoplayDelay || timer || prefersReducedMotion) return;
      if (!hasOverflow()) return;
      timer = setInterval(function () {
        if (viewport.scrollLeft >= maxScroll() - 2) {
          scrollTo(0);
        } else {
          scrollByStep(1);
        }
      }, autoplayDelay);
    }

    function stopAutoplay() {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        stopAutoplay();
        scrollByStep(-1);
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        stopAutoplay();
        scrollByStep(1);
      });
    }

    var dragging = false;
    var moved = false;
    var startX = 0;
    var startScroll = 0;
    var activePointer = null;

    function onPointerDown(event) {
      if (event.pointerType === 'touch') return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (!hasOverflow()) return;
      dragging = true;
      moved = false;
      activePointer = event.pointerId;
      startX = event.clientX;
      startScroll = viewport.scrollLeft;
      viewport.style.scrollSnapType = 'none';
      viewport.style.scrollBehavior = 'auto';
      stopAutoplay();
    }

    function onPointerMove(event) {
      if (!dragging || event.pointerId !== activePointer) return;
      var delta = event.clientX - startX;
      if (!moved && Math.abs(delta) > 4) {
        moved = true;
        root.classList.add('is-dragging');
        if (viewport.setPointerCapture) {
          try {
            viewport.setPointerCapture(activePointer);
          } catch (e) {
          }
        }
      }
      if (!moved) return;
      event.preventDefault();
      viewport.scrollLeft = startScroll - delta;
    }

    function endDrag() {
      if (!dragging) return;
      dragging = false;
      activePointer = null;
      viewport.style.scrollSnapType = '';
      viewport.style.scrollBehavior = '';
      root.classList.remove('is-dragging');
      updateArrows();
      updateDots();
      startAutoplay();
      if (moved) {
        root.addEventListener(
          'click',
          function (e) {
            e.preventDefault();
            e.stopPropagation();
          },
          { capture: true, once: true }
        );
        window.setTimeout(function () {
          moved = false;
        }, 0);
      }
    }

    if (window.PointerEvent) {
      viewport.addEventListener('pointerdown', onPointerDown);
      viewport.addEventListener('pointermove', onPointerMove);
      viewport.addEventListener('pointerup', endDrag);
      viewport.addEventListener('pointercancel', endDrag);
      viewport.addEventListener('pointerleave', endDrag);
      viewport.addEventListener('dragstart', function (e) {
        if (dragging) e.preventDefault();
      });
    }

    viewport.addEventListener(
      'scroll',
      function () {
        window.requestAnimationFrame(function () {
          updateArrows();
          updateDots();
        });
      },
      { passive: true }
    );

    root.addEventListener('mouseenter', stopAutoplay);
    root.addEventListener('mouseleave', startAutoplay);
    root.addEventListener('touchstart', stopAutoplay, { passive: true });
    root.addEventListener('focusin', stopAutoplay);
    root.addEventListener('focusout', startAutoplay);

    function refresh() {
      updateArrows();
      buildDots();
      updateDots();
    }

    window.addEventListener('resize', refresh);

    refresh();
    startAutoplay();

    window.addEventListener('load', refresh);
    window.setTimeout(refresh, 400);
    window.setTimeout(refresh, 1500);
    if (window.ResizeObserver) {
      var ro = new ResizeObserver(function () {
        refresh();
      });
      ro.observe(track);
    }
  }

  function initAll() {
    Array.prototype.forEach.call(document.querySelectorAll('.godard-carousel'), initCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('.godard-carousel');
    if (root) {
      root.dataset.godardCarouselInit = 'false';
      initCarousel(root);
    }
  });

  var recs = document.querySelector('product-recommendations');
  if (recs && window.MutationObserver) {
    new MutationObserver(function () {
      initAll();
    }).observe(recs, { childList: true, subtree: true });
  }
})();
